import { useEffect, useState } from "react";
import { Plane, BedDouble, Ticket, Wrench, MapPin } from "lucide-react";
import api from "../../lib/api";

const KINDS = [
  { key: "flight", label: "Flight", icon: Plane },
  { key: "stay", label: "Stay", icon: BedDouble },
  { key: "event", label: "Event", icon: Ticket },
  { key: "manual", label: "Line item", icon: Wrench },
];

function prefill(item) {
  if (!item) return {};
  if (item.kind === "flight") return {
    date: item.date || "", origin: item.origin || "", destination: item.destination || "",
    airline: item.airlines?.[0] || "",
    cost: item.manual_price && item.est_cost != null ? String(item.est_cost) : "",
  };
  if (item.kind === "stay") return {
    location: item.location || "", check_in: item.check_in || "", check_out: item.check_out || "",
    nightly: item.nightly_estimate != null ? String(item.nightly_estimate) : "",
  };
  if (item.kind === "event") return {
    label: item.label || "", date: item.date || "", location: item.location || "",
    cost: item.est_cost != null ? String(item.est_cost) : "",
  };
  return {
    label: item.label || "", cost: item.est_cost != null ? String(item.est_cost) : "",
    minutes: item.travel_minutes != null ? String(item.travel_minutes) : "",
  };
}

export default function AddItemDialog({ scenarioId, item = null, onClose }) {
  const editing = !!item;
  const [kind, setKind] = useState(item?.kind || "flight");
  const [f, setF] = useState(() => prefill(item));
  const [hint, setHint] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    if (kind !== "flight" || !f.date || f.date.length !== 10) { setHint(null); return; }
    let dead = false;
    api.get(`/scenarios/whereami?date=${f.date}`)
      .then(({ data }) => { if (!dead) setHint(data); })
      .catch(() => { if (!dead) setHint(null); });
    return () => { dead = true; };
  }, [kind, f.date]);

  const submit = async () => {
    setError("");
    setBusy(true);
    const body = editing ? {} : { kind };
    if (kind === "flight") Object.assign(body, {
      origin: f.origin, destination: f.destination, date: f.date,
      airlines: f.airline ? [f.airline] : editing ? [] : undefined,
      est_cost: f.cost !== "" && f.cost != null ? Number(f.cost) : undefined,
    });
    if (kind === "stay") Object.assign(body, {
      location: f.location, check_in: f.check_in, check_out: f.check_out,
      nightly_estimate: f.nightly !== "" && f.nightly != null ? Number(f.nightly) : undefined,
    });
    if (kind === "event") Object.assign(body, {
      label: f.label, location: f.location || undefined, date: f.date,
      est_cost: f.cost !== "" && f.cost != null ? Number(f.cost) : undefined,
    });
    if (kind === "manual") Object.assign(body, {
      label: f.label, est_cost: f.cost !== "" && f.cost != null ? Number(f.cost) : undefined,
      travel_minutes: f.minutes !== "" && f.minutes != null ? Number(f.minutes) : undefined,
    });
    try {
      if (editing) await api.patch(`/scenarios/${scenarioId}/items/${item.id}`, body);
      else await api.post(`/scenarios/${scenarioId}/items`, body);
      onClose(true);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Check the required fields");
      setBusy(false);
    }
  };

  const input = "swiss-input h-10 text-sm";
  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" onClick={() => onClose(false)}>
      <div className="bg-surface border-2 border-ink w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="add-item-dialog">
        <p className="label-tag mb-4">{editing ? `Edit ${item.kind === "manual" ? "line item" : item.kind}` : "Add to scenario"}</p>
        {!editing && (
          <div className="grid grid-cols-4 border border-ink mb-5">
            {KINDS.map(({ key, label, icon: Icon }) => (
              <button key={key} onClick={() => { setKind(key); setError(""); }}
                className={`py-2 font-mono text-[10px] uppercase tracking-wide flex flex-col items-center gap-1 transition-colors ${kind === key ? "bg-black text-white" : "bg-surface text-sand-600 hover:bg-sand-100"}`}
                data-testid={`item-kind-${key}`}>
                <Icon size={13} /> {label}
              </button>
            ))}
          </div>
        )}

        {kind === "flight" && (
          <div className="space-y-3">
            <div>
              <label className="label-tag block mb-1">Date</label>
              <input type="date" className={input} value={f.date || ""} onChange={set("date")} data-testid="item-flight-date" />
              {hint && (
                <div className="mt-1.5 flex items-center gap-2 text-[11px] font-mono text-sand-600" data-testid="whereami-hint">
                  <MapPin size={11} className="text-ember" />
                  {hint.location
                    ? <>You'll be in <b>{hint.location.name}</b>{hint.location.airport ? ` (${hint.location.airport})` : ""}{hint.location.planned ? " — planned" : ""}</>
                    : <>No projected location — home base {hint.suggested_origin || "?"}</>}
                  {hint.suggested_origin && hint.suggested_origin !== f.origin && (
                    <button onClick={() => setF((p) => ({ ...p, origin: hint.suggested_origin }))}
                      className="text-ember underline underline-offset-2" data-testid="use-suggested-origin">
                      use {hint.suggested_origin}
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-tag block mb-1">From (IATA)</label>
                <input className={input} maxLength={3} placeholder="SJU" value={f.origin || ""}
                  onChange={(e) => setF((p) => ({ ...p, origin: e.target.value.toUpperCase() }))} data-testid="item-flight-origin" />
              </div>
              <div>
                <label className="label-tag block mb-1">To (IATA)</label>
                <input className={input} maxLength={3} placeholder="DEN" value={f.destination || ""}
                  onChange={(e) => setF((p) => ({ ...p, destination: e.target.value.toUpperCase() }))} data-testid="item-flight-destination" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-tag block mb-1">Airline filter (optional)</label>
                <input className={input} placeholder="United / UA" value={f.airline || ""} onChange={set("airline")} data-testid="item-flight-airline" />
              </div>
              <div>
                <label className="label-tag block mb-1">Cost ($, optional)</label>
                <input type="number" min="0" className={input} placeholder="0 = miles" value={f.cost || ""} onChange={set("cost")} data-testid="item-flight-cost" />
              </div>
            </div>
            <p className="text-[10px] font-mono text-sand-400">
              {editing
                ? "Changing route or date clears the fetched price — re-price after saving. Leave cost blank for live pricing."
                : "Leave cost blank to fetch live prices · enter 0 for miles bookings"}
            </p>
          </div>
        )}

        {kind === "stay" && (
          <div className="space-y-3">
            <div>
              <label className="label-tag block mb-1">Location</label>
              <input className={input} placeholder="Durango, CO" value={f.location || ""} onChange={set("location")} data-testid="item-stay-location" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-tag block mb-1">Check-in</label>
                <input type="date" className={input} value={f.check_in || ""} onChange={set("check_in")} data-testid="item-stay-checkin" />
              </div>
              <div>
                <label className="label-tag block mb-1">Check-out</label>
                <input type="date" className={input} value={f.check_out || ""} onChange={set("check_out")} data-testid="item-stay-checkout" />
              </div>
            </div>
            <div>
              <label className="label-tag block mb-1">Nightly estimate ($)</label>
              <input type="number" min="0" className={input} placeholder="180" value={f.nightly || ""} onChange={set("nightly")} data-testid="item-stay-nightly" />
            </div>
          </div>
        )}

        {kind === "event" && (
          <div className="space-y-3">
            <div>
              <label className="label-tag block mb-1">Event</label>
              <input className={input} placeholder="Concert — Red Rocks" value={f.label || ""} onChange={set("label")} data-testid="item-event-label" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-tag block mb-1">Date</label>
                <input type="date" className={input} value={f.date || ""} onChange={set("date")} data-testid="item-event-date" />
              </div>
              <div>
                <label className="label-tag block mb-1">Ticket cost ($)</label>
                <input type="number" min="0" className={input} placeholder="120" value={f.cost || ""} onChange={set("cost")} data-testid="item-event-cost" />
              </div>
            </div>
            <div>
              <label className="label-tag block mb-1">Location (optional)</label>
              <input className={input} placeholder="Denver, CO" value={f.location || ""} onChange={set("location")} data-testid="item-event-location" />
            </div>
          </div>
        )}

        {kind === "manual" && (
          <div className="space-y-3">
            <div>
              <label className="label-tag block mb-1">Label</label>
              <input className={input} placeholder="Car rental DEN→Durango" value={f.label || ""} onChange={set("label")} data-testid="item-manual-label" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-tag block mb-1">Cost ($)</label>
                <input type="number" min="0" className={input} placeholder="200" value={f.cost || ""} onChange={set("cost")} data-testid="item-manual-cost" />
              </div>
              <div>
                <label className="label-tag block mb-1">Travel time (min)</label>
                <input type="number" min="0" className={input} placeholder="390" value={f.minutes || ""} onChange={set("minutes")} data-testid="item-manual-minutes" />
              </div>
            </div>
          </div>
        )}

        {error && <div className="mt-3 border border-error bg-red-50 text-error px-3 py-2 text-xs" data-testid="add-item-error">{error}</div>}

        <div className="flex gap-2 mt-6">
          <button onClick={submit} disabled={busy} className="btn-swiss btn-primary flex-1 disabled:opacity-50" data-testid="add-item-confirm">
            {busy ? "..." : editing ? "Save changes" : "Add item"}
          </button>
          <button onClick={() => onClose(false)} className="btn-swiss btn-ghost" data-testid="add-item-cancel">Cancel</button>
        </div>
      </div>
    </div>
  );
}
